import { isMewsSdkReady, openMewsStandalone } from './mews-fallback.js';

// Global components that need to be loaded immediately
const globalImports = [
  {
    path: () => import('./animation-header.js'),
    element: 'animated-header'
  },
  {
    path: () => import('./menu-hash-controller.js'),
    element: 'menu-hash-controller'
  },
  {
    path: () => import('./flodesk-integration.js'),
    element: 'flodesk-form' 
  },
  {
    path: () => import('./newsletter-popup.js'),
    element: 'newsletter-popup' 
  },
  {
    path: () => import('./accordion.js'),
    element: 'kalido-accordion' 
  },
  {
    path: () => import('./carousel.js'),
    element: 'kalido-carousel'
  },
  {
    path: () => import('./form-validation.js'),
    element: 'form-validation'
  },
  {
    path: () => import('./accordion-handler.js'),
    element: 'accordion-handler'
  },
  {
    path: () => import('./services-swiper.js'),
    element: 'services-swiper'
  },
  {
    path: () => import('./modal.js'),
    element: 'kalido-modal'
  },
  {
    path: () => import('./instagram-feed.js'),
    element: 'instagram-feed'
  },
  {
    path: () => import('./text-media-video.js'),
    element: 'text-media-video'
  },
  {
    path: () => import('./hero-banner-video.js'),
    element: 'hero-banner-video'
  }
  // {
  //   path: () => import('./mews-header-visibility.js'),
  //   element: '.mews-distributor'
  // },
];
  
// Dynamic imports for components that are conditionally loaded
const dynamicImports = [
  {
    path: () => import('./apartment-card.js'),
    element: 'apartment-card'
  },
  {
    path: () => import("./booking-form.jsx"),
    element: '.booking-form'
  },
  {
    path: () => import("./banner-gallery.jsx"),
    element: '#banner-gallery'
  },
  {
    path: () => import("./masonry-gallery.jsx"),
    element: '#masonry-gallery'
  },
  {
    path: () => import("./date-form.jsx"),
    element: '#date-form'
  },
  {
    path: () => import("./neighbourhood-map.jsx"),
    element: '#neighbourhood-map'
  },
  {
    path: () => import("./text-image-tabs.js"),
    element: 'tabs-component'
  },
  {
    path: () => import("./lightbox-image-architectural.jsx"),
    element: "#lightbox-image-architectural"
  },
  {
    path: () => import("./custom-audio-player.js"),
    element: "audio-player"
  },
];
  
const loadImports = async (imports) => {
  for (const { path, element } of imports) {
    try {
      // If no element is specified, it's a global import that auto-registers (e.g., custom elements)
      if (!element) {
        console.log("Loading global module");
        await path();
        continue;
      }
      
      // If an element is specified, query for elements in the DOM
      const elements = document.querySelectorAll(element);
      if (elements.length > 0) {
        console.log("Loading module for: " + element);
        const module = await path();
        elements.forEach((el) => {
          if (module.default) {
            // Check if the default export is a class constructor
            if (typeof module.default === 'function' && /^\s*class\s+/.test(module.default.toString())) {
              new module.default(el);
            } else {
              module.default(el);
            }
          }
        });
      }
    } catch (error) {
      console.error(`Error loading module for "${element}":`, error);
    }
  }
};
  
  // Load global imports immediately
  loadImports(globalImports);
  
  // Idempotency guard: Vite emits a non-versioned <link rel="modulepreload"> for
  // main.min.js while the theme also enqueues a versioned <script src=...?v=>. The
  // differing URLs make the browser load and EXECUTE this module twice, which would
  // otherwise register the click listener below twice (=> double window.open on the
  // Mews fallback). Run the Mews wiring at most once per page.
  if (!window.kalidoMewsFallbackWired) {
    window.kalidoMewsFallbackWired = true;

    // Add distributor-open class to all #book-now links
    document.querySelectorAll('a[href="#book-now"]').forEach(link => {
      link.classList.add('distributor-open');
    });

    // Centralized fallback for ALL .distributor-open triggers (vertical Book Now,
    // apartment-info button, footer #book-now links, etc.). When the Mews widget API
    // is genuinely ready, Mews itself opens the embedded widget on these clicks and we
    // do nothing. Otherwise we open the standalone Mews page in a new tab.
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('.distributor-open');
      if (!trigger) return;

      // Gate on whether the Mews widget API is REALLY ready (kalidoOpenMews defined =
      // the async callback completed). NOTE: Mews.Distributor() returning without
      // throwing does NOT mean the widget can open — its async init can fail (seen on
      // staging: the SDK throws an 'appendChild' error) leaving kalidoOpenMews
      // undefined. Gating on that would leave the click dead, so we gate on
      // isMewsSdkReady() instead.
      if (isMewsSdkReady()) return; // Mews opens the embedded widget via openElements; stand down.

      // Mews not ready (pending, failed, or the SDK script never loaded) => fall back to
      // the standalone page. No dates for a generic "Book Now".
      const opened = openMewsStandalone();
      if (opened) {
        // Suppress default navigation: for a[href="#book-now"] this prevents the page
        // jumping to the fragment after opening the tab. Harmless on the <p>/<div> ones.
        event.preventDefault();
      }
    }, false);
  }

  // Load dynamic imports when DOM is ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => loadImports(dynamicImports));
  } else {
    loadImports(dynamicImports);
  }